/**
 * Dashboard - Persona-aware dashboard router
 *
 * Renders the appropriate dashboard based on user persona:
 * - advisor → AdvisorDashboard (BRD-019: consolidated professional/intermediary)
 * - tenant_admin → AdminDashboard
 * - default → HomeDashboard (SCR-002)
 *
 * Note: Dev mode persona switching is available via the floating PersonaIndicator
 * button in the bottom-right corner (see AppLayout).
 */

import { lazy, Suspense } from 'react';
import { usePermissions } from '@myvault/shared/hooks';
import { Spinner } from '@myvault/shared/components';
import { HomeDashboard } from './HomeDashboard';

// Lazy load persona-specific dashboards
// BRD-019: Consolidated professional/intermediary into advisor
const AdvisorDashboard = lazy(() => import('../advisor/Dashboard'));
const TenantAdminDashboard = lazy(() => import('../tenant-admin/TenantAdmin'));

function DashboardSkeleton() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

export function Dashboard() {
  const { hasPersona } = usePermissions();

  // BRD-019: Advisor gets advisor dashboard (consolidated professional/intermediary)
  if (hasPersona('advisor') || hasPersona('professional_accountant') || hasPersona('intermediary_advisor')) {
    return (
      <Suspense fallback={<DashboardSkeleton />}>
        <AdvisorDashboard />
      </Suspense>
    );
  }

  // Tenant admin gets admin dashboard
  if (hasPersona('tenant_admin')) {
    return (
      <Suspense fallback={<DashboardSkeleton />}>
        <TenantAdminDashboard />
      </Suspense>
    );
  }

  // Default: consumer dashboard (SCR-002 HomeDashboard)
  return <HomeDashboard />;
}

export default Dashboard;
